import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'

import { Slider } from '#components/Slider'
import { useFetch } from '#hooks/useAdminResource.js'
import { useDebounce } from '#hooks/useDebounce.js'

import { TraceFilterHelp } from './TraceFilterHelp'

interface TraceFilterInputProps {
  value: string
  onApply: (value: string) => void
}

type FilterValidation =
  | { ok: true }
  | { ok: false; error: string; line: number; column: number }

const PLACEHOLDER = `request.body.accessorials contains "EXLG13"
and response.rates[0].total > 500`

export const TraceFilterInput = ({ value, onApply }: TraceFilterInputProps) => {
  const [draft, setDraft] = useState(value)
  const [isHelpOpen, setIsHelpOpen] = useState(false)

  useEffect(() => {
    setDraft(value)
  }, [value])

  const trimmed = draft.trim()
  const debounced = useDebounce(trimmed, 300)
  const { data: validation } = useFetch<FilterValidation>(
    'traces/filter/validate',
    { filter: debounced },
    { enabled: !!debounced },
  )

  // Ignore results for text that has since changed
  const parseError =
    debounced === trimmed && validation?.ok === false ? validation : undefined
  const canApply = !parseError && trimmed !== value

  const apply = () => {
    if (canApply) {
      onApply(trimmed)
    }
  }

  return (
    <div className="form-control w-full">
      <div className="label">
        <span className="label-text">Advanced filter</span>
        <span className="label-text-alt flex items-center gap-3">
          <span className="opacity-60">⌘/Ctrl+Enter to apply</span>
          <button
            type="button"
            className="btn btn-xs btn-ghost gap-1"
            onClick={() => setIsHelpOpen(true)}
          >
            <QuestionMarkCircleIcon className="w-4 h-4" />
            Syntax help
          </button>
        </span>
      </div>
      <textarea
        className={`textarea textarea-bordered w-full font-mono text-sm ${parseError ? 'textarea-error' : ''}`}
        rows={3}
        spellCheck={false}
        placeholder={PLACEHOLDER}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            apply()
          }
        }}
      />
      <div className="label">
        <span className="label-text-alt text-error">
          {parseError &&
            `Line ${parseError.line}, column ${parseError.column}: ${parseError.error}`}
        </span>
        <div className="flex gap-2">
          {value && (
            <button
              type="button"
              className="btn btn-xs btn-ghost"
              onClick={() => onApply('')}
            >
              Clear
            </button>
          )}
          <button
            type="button"
            className="btn btn-xs btn-primary"
            disabled={!canApply}
            onClick={apply}
          >
            Apply
          </button>
        </div>
      </div>

      <Slider
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title="Advanced filter syntax"
      >
        <TraceFilterHelp />
      </Slider>
    </div>
  )
}
